/**
 * CalendarPage: FullCalendar v5 mit Resource-TimeGrid.
 *
 * Räume = Resources (Spalten), Zeit = Zeile, Sessions = Events.
 * Drag&Drop via interaction-Plugin.
 *
 * v5 (statt v6) weil v6 interne Klassen-Component-Inkompat mit React 18 hat.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { EventInput } from "@fullcalendar/core";
import { api, ApiCallError, type CourseSession, type Location, type Room } from "../lib/api";
import { Drawer } from "../components/Drawer";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { SessionStatusBadge, SESSION_STATUS_LABEL } from "../components/Badge";
import { Button } from "../components/Button";
import { AvailabilitySelect } from "../components/AvailabilitySelect";
import { SessionAvailabilityHint, useAvailabilityMap } from "../components/SessionAvailabilityHint";
import { useAuth } from "../lib/auth";
import { buildCalendarScheduleUpdate, NO_ROOM_RESOURCE_ID } from "../lib/calendarSchedule";
import { formatCalendarSelectionTimes, isCompactCalendarEvent } from "../lib/calendarEventPresentation";
import { getYearSessionDensity, type YearSessionDensity } from "../lib/yearOverview";
import { formatYearOverviewDate, getIsoWeek, getIsoWeeksInYear, getIsoWeekStart, getYearOverviewDayTarget } from "../lib/calendarNavigation";

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  entwurf: { bg: "#f1f5f9", border: "#94a3b8" },
  geplant: { bg: "#dbeafe", border: "#3b82f6" },
  abgeschlossen: { bg: "#d1fae5", border: "#10b981" },
  storniert: { bg: "#ffe4e6", border: "#f43f5e" },
};

const STATUSES: Array<"entwurf" | "geplant" | "abgeschlossen" | "storniert"> = [
  "entwurf",
  "geplant",
  "abgeschlossen",
  "storniert",
];

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

function isCurrentLocalDay(date: Date): boolean {
  const today = new Date();
  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate();
}

const YEAR_OVERVIEW_MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const CALENDAR_MONTHS = YEAR_OVERVIEW_MONTHS;
const YEAR_OVERVIEW_WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function densityColor(count: number): string {
  if (count === 0) return "#f8fafc";
  if (count === 1) return "#dbeafe";
  if (count === 2) return "#93c5fd";
  if (count === 3) return "#3b82f6";
  return "#1d4ed8";
}

function yearDayKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function YearOverview({ year, density, onDaySelect }: { year: number; density: YearSessionDensity; onDaySelect: (year: number, month: number, day: number) => void }) {
  return (
    <section aria-label={`Jahresübersicht ${year}`} className="rounded-lg border border-slate-200 bg-white p-2">
      <div className="grid grid-cols-2 gap-x-2 gap-y-3 sm:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-12">
        {YEAR_OVERVIEW_MONTHS.map((monthName, month) => {
          const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          return (
            <div key={monthName} className="min-w-0">
              <h3 className="mb-0.5 text-[11px] font-semibold text-slate-700">{monthName}</h3>
              <div className="grid grid-cols-7 gap-px text-center text-[8px] leading-3 text-slate-400" aria-hidden="true">
                {YEAR_OVERVIEW_WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
              </div>
              <div className="mt-px grid grid-cols-7 gap-px">
                {Array.from({ length: firstWeekday }, (_, index) => <span key={`empty-${index}`} />)}
                {Array.from({ length: daysInMonth }, (_, index) => {
                  const day = index + 1;
                  const key = yearDayKey(year, month, day);
                  const count = density[key] ?? 0;
                  const densityLabel = `${formatYearOverviewDate(year, month, day)}: ${count} ${count === 1 ? "Session" : "Sessions"}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      title={`${densityLabel} · Tagesansicht öffnen`}
                      aria-label={`${densityLabel}. Tagesansicht öffnen`}
                      onClick={() => onDaySelect(year, month, day)}
                      className="flex aspect-square min-h-3 cursor-pointer items-center justify-center rounded-[2px] border-0 p-0 text-[8px] leading-none text-slate-700 transition hover:brightness-95 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600"
                      style={{ backgroundColor: densityColor(count) }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function CalendarPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filterLoc, setFilterLoc] = useState<string>("");
  const [toast, setToast] = useState<{ type: "ok" | "err"; message: string } | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);
  const calendarShellRef = useRef<HTMLDivElement | null>(null);
  const [calendarTitle, setCalendarTitle] = useState("Kalender");
  const [calendarView, setCalendarView] = useState("resourceTimeGridDay");
  const [calendarMonthIndex, setCalendarMonthIndex] = useState(() => new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());
  const [calendarDay, setCalendarDay] = useState(() => new Date().getDate());
  const [calendarWeek, setCalendarWeek] = useState(() => getIsoWeek(new Date()).week);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<CourseSession | null>(null);

  const [quickOpen, setQuickOpen] = useState(false);
  const [quickRoomId, setQuickRoomId] = useState<number | null>(null);
  const [quickStart, setQuickStart] = useState<string>("");
  const [quickEnd, setQuickEnd] = useState<string>("");
  const [quickTitle, setQuickTitle] = useState<string>("");
  const [quickError, setQuickError] = useState<string | null>(null);
  const [quickSaving, setQuickSaving] = useState(false);

  const canWrite = user?.role === "admin" || user?.role === "planer";

  const locQ = useQuery({ queryKey: ["locations"], queryFn: () => api.listLocations() });
  const locations: Location[] = locQ.data?.locations ?? [];

  const roomQ = useQuery({
    queryKey: ["rooms", filterLoc],
    queryFn: () => api.listRooms(filterLoc ? Number(filterLoc) : undefined),
  });
  const allRooms: Room[] = roomQ.data?.rooms ?? [];

  const sessQ = useQuery({
    queryKey: ["sessions", filterLoc],
    queryFn: () => api.listSessions({ locationId: filterLoc ? Number(filterLoc) : undefined }),
  });
  const sessions: CourseSession[] = sessQ.data?.sessions ?? [];
  const currentYear = new Date().getFullYear();
  const [overviewYear, setOverviewYear] = useState(currentYear);
  const yearDensity = useMemo(
    () => getYearSessionDensity(overviewYear, sessions),
    [overviewYear, sessions],
  );
  // Inaktive Räume bleiben als Spalte sichtbar, solange sie noch Sessions tragen.
  const rooms = allRooms.filter(
    (room) => room.isActive || sessions.some((session) => session.roomId === room.id)
  );

  const resources = useMemo(
    () => {
      const roomResources = rooms.map((r) => ({
        id: String(r.id),
        title: `${r.name}${r.isActive ? "" : " · inaktiv"}`,
        extendedProps: { room: r, kind: "room" as const },
      }));
      // Sentinel-Resource für Sessions ohne Raum. Wird nur in Resource-Views
      // angezeigt, nicht in der Monatsansicht (dort gibt es keine Resources).
      const noRoom = {
        id: NO_ROOM_RESOURCE_ID,
        title: "Ohne Raum",
        extendedProps: { kind: "no-room" as const },
        // Visuell hervorheben
        eventBackgroundColor: "#fef3c7",
        eventBorderColor: "#f59e0b",
      };
      return [...roomResources, noRoom];
    },
    [rooms]
  );

  // Sessions mit ermitteltem Raum (eagerly fetched: wir nehmen einfach den
  // ersten Raum mit locationId der Session — korrekter Wert kommt per getSession,
  // das hier ist Best-Effort für die initiale Darstellung).
  const events: EventInput[] = useMemo(
    () =>
      sessions.map((s) => {
        const color = STATUS_COLORS[s.status] ?? STATUS_COLORS.entwurf!;
        // Sessions ohne Raum landen in der "Ohne Raum"-Sentinel-Resource
        // (sonst wären sie in der Resource-View unsichtbar).
        const useNoRoom = s.roomId == null;
        return {
          id: String(s.id),
          // resourceId ist nötig damit FullCalendar die Session in die richtige
          // Resource-Spalte einsortiert.
          resourceId: useNoRoom ? NO_ROOM_RESOURCE_ID : String(s.roomId),
          title: s.title,
          start: new Date(s.plannedStart),
          end: new Date(s.plannedEnd),
          backgroundColor: useNoRoom ? "#fef3c7" : color.bg,
          borderColor: useNoRoom ? "#f59e0b" : color.border,
          textColor: "#0f172a",
          extendedProps: { session: s, noRoom: useNoRoom },
        };
      }),
    [sessions]
  );
  const calendarYears = useMemo(
    () => Array.from({ length: 21 }, (_, index) => calendarYear - 10 + index),
    [calendarYear],
  );
  const calendarDays = useMemo(
    () => Array.from({ length: new Date(calendarYear, calendarMonthIndex + 1, 0).getDate() }, (_, index) => index + 1),
    [calendarYear, calendarMonthIndex],
  );
  const calendarWeeks = useMemo(
    () => Array.from({ length: getIsoWeeksInYear(calendarYear) }, (_, index) => index + 1),
    [calendarYear],
  );

  function showToast(t: { type: "ok" | "err"; message: string }) {
    setToast(t);
    setTimeout(() => setToast(null), 4000);
  }

  async function handleEventDrop(info: { event: { id: string; start: Date | null; end: Date | null }; newResource?: { id: string } | null; revert: () => void }) {
    const sessionId = Number(info.event.id);
    const update = buildCalendarScheduleUpdate({
      start: info.event.start,
      end: info.event.end,
      resourceId: info.newResource?.id,
    });
    if (!update) {
      info.revert();
      return;
    }
    try {
      await api.updateSessionSchedule(sessionId, update);
      qc.invalidateQueries({ queryKey: ["sessions"] });
      showToast({ type: "ok", message: "Session verschoben" });
    } catch (e) {
      info.revert();
      showToast({ type: "err", message: e instanceof ApiCallError ? e.message : "Verschieben fehlgeschlagen" });
    }
  }

  async function handleEventResize(info: { event: { id: string; start: Date | null; end: Date | null }; revert: () => void }) {
    const sessionId = Number(info.event.id);
    const newStart = info.event.start;
    const newEnd = info.event.end;
    if (!newStart || !newEnd) { info.revert(); return; }
    try {
      await api.updateSession(sessionId, { plannedStart: newStart.toISOString(), plannedEnd: newEnd.toISOString() });
      qc.invalidateQueries({ queryKey: ["sessions"] });
      showToast({ type: "ok", message: "Dauer geändert" });
    } catch (e) {
      info.revert();
      showToast({ type: "err", message: e instanceof ApiCallError ? e.message : "Ändern fehlgeschlagen" });
    }
  }

  function handleEventClick(info: { event: { id: string } }) {
    const s = sessions.find((x) => x.id === Number(info.event.id));
    if (s) {
      setEditing(s);
      setEditOpen(true);
    }
  }

  function handleSelect(info: { resource?: { id: string } | null; start: Date; end: Date; view: { calendar: { unselect: () => void } } }) {
    if (!canWrite) { info.view.calendar.unselect(); return; }
    const resourceId = info.resource?.id;
    const roomId = resourceId && resourceId !== NO_ROOM_RESOURCE_ID ? Number(resourceId) : null;
    if (roomId && !rooms.find((room) => room.id === roomId)?.isActive) {
      showToast({ type: "err", message: "Inaktive Räume können nicht neu verplant werden." });
      info.view.calendar.unselect();
      return;
    }
    setQuickRoomId(roomId);
    setQuickStart(toLocalInput(info.start.toISOString()));
    setQuickEnd(toLocalInput(info.end.toISOString()));
    setQuickTitle("");
    setQuickError(null);
    setQuickOpen(true);
  }

  async function submitQuick(e: React.FormEvent) {
    e.preventDefault();
    setQuickError(null);
    if (!quickTitle.trim()) return setQuickError("Titel ist erforderlich");
    if (!quickRoomId) return setQuickError("Bitte einen Raum wählen");
    setQuickSaving(true);
    try {
      const room = rooms.find((r) => r.id === quickRoomId);
      if (!room) throw new Error("Raum nicht gefunden");
      const created = await api.createSession({
        title: quickTitle.trim(),
        locationId: room.locationId,
        plannedStart: fromLocalInput(quickStart),
        plannedEnd: fromLocalInput(quickEnd),
        status: "entwurf",
      });
      await api.assignSessionRoom(created.session.id, quickRoomId);
      qc.invalidateQueries({ queryKey: ["sessions"] });
      setQuickOpen(false);
      showToast({ type: "ok", message: "Session angelegt" });
    } catch (e) {
      setQuickError(e instanceof ApiCallError ? e.message : "Fehler");
    } finally {
      setQuickSaving(false);
    }
  }

  function changeCalendarView(view: "resourceTimeGridDay" | "resourceTimeGridWeek" | "dayGridMonth") {
    calendarRef.current?.getApi().changeView(view);
    setCalendarView(view);
  }

  function navigateCalendar(direction: "prev" | "next" | "today") {
    calendarRef.current?.getApi()[direction]();
  }

  function selectCalendarPeriod(year: number, monthIndex: number) {
    if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11) return;
    calendarRef.current?.getApi().gotoDate(new Date(year, monthIndex, 1));
    setCalendarYear(year);
    setCalendarMonthIndex(monthIndex);
  }

  function selectCalendarWeek(year: number, week: number) {
    if (!Number.isInteger(year) || week < 1 || week > getIsoWeeksInYear(year)) return;
    calendarRef.current?.getApi().gotoDate(getIsoWeekStart(year, week));
    setCalendarYear(year);
    setCalendarWeek(week);
  }

  function selectCalendarDay(year: number, monthIndex: number, day: number) {
    const maxDay = new Date(year, monthIndex + 1, 0).getDate();
    if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11 || day < 1 || day > maxDay) return;
    calendarRef.current?.getApi().gotoDate(new Date(year, monthIndex, day));
    setCalendarYear(year);
    setCalendarMonthIndex(monthIndex);
    setCalendarDay(day);
  }

  function openYearOverviewDay(year: number, monthIndex: number, day: number) {
    const target = getYearOverviewDayTarget(year, monthIndex, day);
    const calendar = calendarRef.current?.getApi();
    if (!target || !calendar) return;
    calendar.gotoDate(target.date);
    calendar.changeView(target.view);
    setCalendarView(target.view);
    setCalendarYear(year);
    setCalendarMonthIndex(monthIndex);
    setCalendarDay(day);
    calendarShellRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">Kalender</h1>
        <select
          value={filterLoc}
          onChange={(e) => setFilterLoc(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Alle Standorte</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {toast && (
        <div className={`rounded-md border px-3 py-2 text-sm ${toast.type === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          {toast.message}
        </div>
      )}

      <div ref={calendarShellRef} className="calendar-shell rounded-lg border border-slate-200 bg-white p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-1" aria-label="Kalenderansicht">
            {[
              ["resourceTimeGridDay", "Tag"],
              ["resourceTimeGridWeek", "Woche"],
              ["dayGridMonth", "Monat"],
            ].map(([view, label]) => (
              <button
                key={view}
                type="button"
                onClick={() => changeCalendarView(view as "resourceTimeGridDay" | "resourceTimeGridWeek" | "dayGridMonth")}
                className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${calendarView === view ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex min-w-0 items-center gap-1.5">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => navigateCalendar("prev")} aria-label="Vorheriger Zeitraum">←</Button>
            <div className="min-w-32 text-center">
              {calendarView === "dayGridMonth" ? (
                <div className="flex items-center gap-1" aria-label="Monat und Jahr auswählen">
                  <select value={calendarMonthIndex} onChange={(event) => setCalendarMonthIndex(Number(event.target.value))} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Monat auswählen">
                    {CALENDAR_MONTHS.map((month, index) => <option key={month} value={index}>{month}</option>)}
                  </select>
                  <select value={calendarYear} onChange={(event) => setCalendarYear(Number(event.target.value))} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Jahr auswählen">
                    {calendarYears.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                  <Button type="button" variant="secondary" size="sm" className="h-8 px-2 text-xs" onClick={() => selectCalendarPeriod(calendarYear, calendarMonthIndex)}>Anzeigen</Button>
                </div>
              ) : calendarView === "resourceTimeGridWeek" ? (
                <div className="flex items-center gap-1" aria-label="Kalenderwoche und Jahr auswählen">
                  <select value={calendarWeek} onChange={(event) => setCalendarWeek(Number(event.target.value))} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Kalenderwoche auswählen">
                    {calendarWeeks.map((week) => <option key={week} value={week}>KW {week}</option>)}
                  </select>
                  <select value={calendarYear} onChange={(event) => { const year = Number(event.target.value); setCalendarYear(year); setCalendarWeek((week) => Math.min(week, getIsoWeeksInYear(year))); }} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Jahr auswählen">
                    {calendarYears.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                  <Button type="button" variant="secondary" size="sm" className="h-8 px-2 text-xs" onClick={() => selectCalendarWeek(calendarYear, calendarWeek)}>Anzeigen</Button>
                </div>
              ) : calendarView === "resourceTimeGridDay" ? (
                <div className="flex items-center gap-1" aria-label="Tag, Monat und Jahr auswählen">
                  <select value={calendarDay} onChange={(event) => setCalendarDay(Number(event.target.value))} className="w-14 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Tag auswählen">
                    {calendarDays.map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                  <select value={calendarMonthIndex} onChange={(event) => { const month = Number(event.target.value); setCalendarMonthIndex(month); setCalendarDay((day) => Math.min(day, new Date(calendarYear, month + 1, 0).getDate())); }} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Monat auswählen">
                    {CALENDAR_MONTHS.map((month, index) => <option key={month} value={index}>{month}</option>)}
                  </select>
                  <select value={calendarYear} onChange={(event) => { const year = Number(event.target.value); setCalendarYear(year); setCalendarDay((day) => Math.min(day, new Date(year, calendarMonthIndex + 1, 0).getDate())); }} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Jahr auswählen">
                    {calendarYears.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                  <Button type="button" variant="secondary" size="sm" className="h-8 px-2 text-xs" onClick={() => selectCalendarDay(calendarYear, calendarMonthIndex, calendarDay)}>Anzeigen</Button>
                </div>
              ) : (
                <div className="px-2 py-1 text-sm font-semibold text-slate-800" aria-live="polite">{calendarTitle}</div>
              )}
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => navigateCalendar("next")} aria-label="Nächster Zeitraum">→</Button>
            <Button type="button" variant="secondary" size="sm" className="h-8 px-2.5 text-xs" onClick={() => navigateCalendar("today")}>Heute</Button>
          </div>
        </div>
        {roomQ.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade Räume…</div>
        ) : rooms.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine aktiven Räume für den gewählten Standort.</div>
        ) : (
          <FullCalendar
            ref={calendarRef}
            // key ändert sich bei jedem Query-Refetch → zwingt FullCalendar
            // zum Re-Mount, sodass die events-Prop frisch übernommen wird.
            key={`fc-${sessions.length}-${rooms.length}`}
            plugins={[resourceTimeGridPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin]}
            initialView="resourceTimeGridDay"
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            resources={resources}
            events={events}
            height="auto"
            slotMinTime="07:00:00"
            slotMaxTime="20:00:00"
            slotDuration="00:30:00"
            nowIndicator
            editable={canWrite}
            eventStartEditable={canWrite}
            eventDurationEditable={canWrite}
            selectable={canWrite}
            selectMirror
            dayMaxEvents
            allDaySlot={false}
            datesSet={(info) => {
              setCalendarTitle(info.view.title);
              setCalendarView(info.view.type);
              const displayedDate = info.view.currentStart;
              const isoWeek = getIsoWeek(displayedDate);
              setCalendarYear(info.view.type === "resourceTimeGridWeek" ? isoWeek.year : displayedDate.getFullYear());
              setCalendarMonthIndex(displayedDate.getMonth());
              setCalendarDay(displayedDate.getDate());
              setCalendarWeek(isoWeek.week);
            }}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventClick={handleEventClick}
            select={handleSelect}
            locale="de"
            firstDay={1}
            dayCellClassNames={(arg) => isCurrentLocalDay(arg.date) ? ["calendar-current-day"] : []}
            dayHeaderClassNames={(arg) => isCurrentLocalDay(arg.date) ? ["calendar-current-day"] : []}
            buttonText={{ today: "Heute", day: "Tag", week: "Woche", month: "Monat" }}
            headerToolbar={false}
            eventContent={(arg) => {
              // FullCalendar ruft eventContent auch für interne Drag-Previews
              // und Time-Indicator auf — extendedProps kann dann fehlen.
              // Defensiv: Fallback auf Title-only.
              const s = arg.event.extendedProps?.session as CourseSession | undefined;
              const start = arg.event.start;
              const time = start ? start.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
              const selectionTimes = s ? null : formatCalendarSelectionTimes(start, arg.event.end);
              if (selectionTimes) {
                return (
                  <div className="flex h-full min-w-0 flex-col justify-between overflow-hidden px-1 py-0.5 text-xs font-normal leading-tight tabular-nums text-white">
                    <span className="whitespace-nowrap">{selectionTimes.startTime}</span>
                    <span className="whitespace-nowrap">{selectionTimes.endTime}</span>
                  </div>
                );
              }
              if (isCompactCalendarEvent(start, arg.event.end)) {
                return (
                  <div className="flex h-full min-w-0 items-center gap-1 overflow-hidden px-1 text-[11px] leading-none">
                    <span className="shrink-0 text-slate-600">{time}</span>
                    <span className="truncate font-medium">{arg.event.title}</span>
                  </div>
                );
              }
              return (
                <div className="h-full overflow-hidden px-1 py-0.5 text-xs leading-tight">
                  <div className="truncate font-medium">{arg.event.title}</div>
                  {s ? (
                    <>
                      <div className="truncate text-slate-600">{time} · {s.participantCount} TN</div>
                      <SessionStatusBadge status={s.status} />
                    </>
                  ) : (
                    <div className="truncate text-slate-500">{time}</div>
                  )}
                </div>
              );
            }}
          />
        )}
      </div>

      <section aria-labelledby="year-overview-heading" className="space-y-3 border-t border-slate-200 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 id="year-overview-heading" className="text-lg font-semibold">Jahresübersicht</h2>
            <p className="text-sm text-slate-500">Analyseansicht · Termindichte pro Tag, ohne stornierte Sessions. Tag anklicken für die Raum-Tagesansicht.</p>
          </div>
          <div className="flex min-w-0 items-center gap-1.5" aria-label="Jahr auswählen">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setOverviewYear((year) => year - 1)} aria-label="Vorheriges Jahr anzeigen">←</Button>
            <span className="min-w-32 text-center text-sm font-semibold text-slate-800 tabular-nums" aria-live="polite">{overviewYear}</span>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setOverviewYear((year) => year + 1)} aria-label="Nächstes Jahr anzeigen">→</Button>
            <Button type="button" variant="secondary" size="sm" className="h-8 px-2.5 text-xs" onClick={() => setOverviewYear(currentYear)} disabled={overviewYear === currentYear}>Aktuelles Jahr</Button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600" aria-label="Legende Termindichte">
          <span>Weniger</span>
          {[0, 1, 2, 3, 4].map((count) => <span key={count} className="h-3 w-3 rounded-sm" style={{ backgroundColor: densityColor(count) }} />)}
          <span>Mehr</span>
        </div>
        {sessQ.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Lade Termindichte…</div>
        ) : (
          <YearOverview year={overviewYear} density={yearDensity} onDaySelect={openYearOverviewDay} />
        )}
      </section>

      <Drawer open={quickOpen} onClose={() => setQuickOpen(false)} title="Neue Session">
        <form onSubmit={submitQuick} className="space-y-4">
          {quickError && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{quickError}</div>}
          <div>
            <Label htmlFor="qt">Titel *</Label>
            <Input id="qt" value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)} required autoFocus />
          </div>
          <div>
            <Label htmlFor="qr">Raum *</Label>
            <select id="qr" value={quickRoomId ?? ""} onChange={(e) => setQuickRoomId(Number(e.target.value) || null)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required>
              <option value="">— bitte wählen —</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="qs">Start</Label><Input id="qs" type="datetime-local" value={quickStart} onChange={(e) => setQuickStart(e.target.value)} required /></div>
            <div><Label htmlFor="qe">Ende</Label><Input id="qe" type="datetime-local" value={quickEnd} onChange={(e) => setQuickEnd(e.target.value)} required /></div>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setQuickOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={quickSaving}>{quickSaving ? "Lege an…" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>

      {editing && (
        <SessionEditDrawer
          key={editing.id}
          session={editing}
          open={editOpen}
          onClose={() => { setEditOpen(false); setEditing(null); }}
          onChanged={() => qc.invalidateQueries({ queryKey: ["sessions"] })}
        />
      )}
    </div>
  );
}

function SessionEditDrawer({ session, open, onClose, onChanged }: { session: CourseSession; open: boolean; onClose: () => void; onChanged: () => void }) {
  const [title, setTitle] = useState(session.title);
  const [plannedStart, setPlannedStart] = useState(toLocalInput(session.plannedStart));
  const [plannedEnd, setPlannedEnd] = useState(toLocalInput(session.plannedEnd));
  const [status, setStatus] = useState<typeof STATUSES[number]>(session.status);
  const [participantCount, setParticipantCount] = useState(session.participantCount.toString());
  const [notes, setNotes] = useState(session.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sessQ = useQuery({
    queryKey: ["session", session.id],
    queryFn: () => api.getSession(session.id),
    enabled: open,
  });
  const full = sessQ.data?.session;
  const [roomId, setRoomId] = useState<string>("");
  const [trainerIds, setTrainerIds] = useState<number[]>([]);
  const [resourceIds, setResourceIds] = useState<number[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Init aus full wenn geladen — in useEffect um Render-Time-State-Updates zu vermeiden.
  useEffect(() => {
    if (full && !initialized) {
      setRoomId(full.room?.id?.toString() ?? "");
      setTrainerIds(full.trainers.map((t) => t.id));
      setResourceIds(full.resources.map((r) => r.id));
      setInitialized(true);
    }
  }, [full, initialized]);

  const roomsQ = useQuery({ queryKey: ["rooms", session.locationId], queryFn: () => api.listRooms(session.locationId), enabled: open });
  const trainersQ = useQuery({ queryKey: ["trainers"], queryFn: () => api.listTrainers(), enabled: open });
  const resourcesQ = useQuery({ queryKey: ["resources", session.locationId], queryFn: () => api.listResources({ locationId: session.locationId }), enabled: open });
  const rooms: Room[] = roomsQ.data?.rooms ?? [];
  const availability = useAvailabilityMap([{ plannedStart, plannedEnd }], session.id, fromLocalInput);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!initialized || !full) return;
    if (!title.trim()) {
      setError("Titel ist erforderlich");
      return;
    }
    if (!plannedStart || !plannedEnd || new Date(plannedStart) >= new Date(plannedEnd)) {
      setError("Endzeit muss nach Startzeit liegen");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.updateSession(session.id, { title: title.trim(), status, participantCount: Number(participantCount) || 0, notes: notes.trim() || undefined });
      const roomChanged = roomId !== (full.room?.id?.toString() ?? "");
      const timeChanged = plannedStart !== toLocalInput(full.plannedStart) || plannedEnd !== toLocalInput(full.plannedEnd);
      if (timeChanged || roomChanged) {
        await api.updateSessionSchedule(session.id, {
          plannedStart: fromLocalInput(plannedStart),
          plannedEnd: fromLocalInput(plannedEnd),
          ...(roomChanged ? { roomId: roomId ? Number(roomId) : null } : {}),
        });
      }
      const currentTrainerIds = (full?.trainers ?? []).map((t) => t.id);
      const sameTrainers = currentTrainerIds.length === trainerIds.length && currentTrainerIds.every((x) => trainerIds.includes(x));
      if (!sameTrainers) await api.assignSessionTrainers(session.id, trainerIds, true);
      const currentResIds = (full?.resources ?? []).map((r) => r.id);
      const sameRes = currentResIds.length === resourceIds.length && currentResIds.every((x) => resourceIds.includes(x));
      if (!sameRes) await api.assignSessionResources(session.id, resourceIds);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof ApiCallError ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={`Session bearbeiten: ${session.title}`} width="32rem">
      <form onSubmit={save} className="space-y-4">
        {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <div>
          <Label htmlFor="et">Titel *</Label>
          <Input id="et" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="estart">Start</Label>
            <Input id="estart" type="datetime-local" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="eend">Ende</Label>
            <Input id="eend" type="datetime-local" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="es">Status</Label>
            <select id="es" value={status} onChange={(e) => setStatus(e.target.value as typeof STATUSES[number])} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{SESSION_STATUS_LABEL[s]}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="ep">Teilnehmer</Label>
            <Input id="ep" type="number" min="0" value={participantCount} onChange={(e) => setParticipantCount(e.target.value)} />
          </div>
        </div>
        <AvailabilitySelect id="er" label="Raum" value={roomId} onChange={setRoomId} placeholder="— keiner —" conflictedIds={availability.roomIds} options={rooms.filter((room) => room.isActive || room.id === Number(roomId)).map((room) => ({ id: room.id, label: room.name, inactive: !room.isActive }))} />
        <div>
          <Label>Trainer</Label>
          <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2">
            {(trainersQ.data?.trainers ?? []).filter((t) => t.isActive || trainerIds.includes(t.id)).map((t) => (
              <label key={t.id} className={`flex items-center gap-2 rounded px-1 py-1 text-sm ${availability.trainerIds.includes(t.id) ? "bg-rose-50 text-rose-900" : ""}`}>
                <input type="checkbox" checked={trainerIds.includes(t.id)} onChange={(e) => setTrainerIds((cur) => e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id))} className="h-4 w-4 rounded border-slate-300" />
                <span>{t.fullName}{t.isActive ? "" : " (inaktiv)"}{availability.trainerIds.includes(t.id) && <span className="ml-1 text-xs text-rose-700">Im gewählten Zeitraum belegt</span>}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>Ressourcen</Label>
          <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 p-2">
            {(resourcesQ.data?.resources ?? []).filter((r) => r.isActive || resourceIds.includes(r.id)).map((r) => (
              <label key={r.id} className={`flex items-center gap-2 rounded px-1 py-1 text-sm ${availability.resourceIds.includes(r.id) ? "bg-rose-50 text-rose-900" : ""}`}>
                <input type="checkbox" checked={resourceIds.includes(r.id)} onChange={(e) => setResourceIds((cur) => e.target.checked ? [...cur, r.id] : cur.filter((x) => x !== r.id))} className="h-4 w-4 rounded border-slate-300" />
                <span>{r.name}{r.isActive ? "" : " (inaktiv)"}{availability.resourceIds.includes(r.id) && <span className="ml-1 text-xs text-rose-700">Im gewählten Zeitraum belegt</span>}</span>
              </label>
            ))}
          </div>
        </div>
        <SessionAvailabilityHint
          plannedStart={plannedStart}
          plannedEnd={plannedEnd}
          roomIds={roomId ? [Number(roomId)] : []}
          trainerIds={trainerIds}
          resourceIds={resourceIds}
          excludeSessionId={session.id}
          toIso={fromLocalInput}
        />
        <div>
          <Label htmlFor="en">Notizen</Label>
          <Input id="en" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <Button variant="secondary" type="button" onClick={onClose}>Abbrechen</Button>
          <Button type="submit" disabled={saving || !initialized}>{saving ? "Speichere…" : !initialized ? "Lade…" : "Speichern"}</Button>
        </div>
      </form>
    </Drawer>
  );
}
